import type { Theme } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Alert,
    Box,
    Button,
    Col,
    Form,
    FormGroup,
    FormParagraph,
    FormSection,
    Modal,
    Row,
    Text,
} from '@ksyos/design-system';
import type { MaritalStatus, State } from '@common/enums';
import type { IInsuranceHistory, IOption } from '@frontend/components/interfaces';
import { getSelectedLanguage } from '@frontend/translate';
import type { IPatient } from './PatientsAddEdit';

interface IPatientDeceasedEditFormProps {
    activeInsurance: IInsuranceHistory | undefined;
    deceasedDate: string;
    getCountryOption: (country: string | null) =>
        | {
              label: string | undefined;
              value: string;
          }
        | undefined;
    getMaritalStatusOption: (maritalStatus: MaritalStatus | null) =>
        | {
              label: string;
              value: string;
          }
        | undefined;
    getPractitionerAndOrganizationOption: (
        organizationId: string,
        practitionerId: string
    ) => IOption<string> | undefined;
    getStateOption: (state: string | null) =>
        | {
              label: State | undefined;
              value: string;
          }
        | undefined;
    handleSubmit: () => Promise<void>;
    isSaving: boolean;
    loadedPatient: IPatient;
    onClose: () => Promise<void> | void;
    setDeceasedDate: React.Dispatch<React.SetStateAction<string | null>>;
}

export function PatientDeceasedEditForm(props: IPatientDeceasedEditFormProps) {
    const {
        activeInsurance,
        deceasedDate,
        getCountryOption,
        getMaritalStatusOption,
        getPractitionerAndOrganizationOption,
        getStateOption,
        handleSubmit,
        isSaving,
        loadedPatient,
        onClose,
        setDeceasedDate,
    } = props;

    return (
        <Form
            onSubmit={async (e) => {
                e.preventDefault();
                await handleSubmit();
            }}
            submitOnEnter={false}
        >
            <Modal.Body>
                <Alert appearance="info">
                    <Box
                        alignItems="center"
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="space-between"
                    >
                        <Text
                            fontWeight="bold"
                            size="md"
                            sx={{
                                color: (theme: Theme) => theme.alert.info.foregroundColor,
                            }}
                        >
                            {`Sinds ${DateTime.from(deceasedDate).format('DD-MM-YYYY')} is deze patiënt als overleden
                                geregistreerd.`}
                        </Text>
                        <Alert.Button onClick={() => setDeceasedDate(null)}>
                            {getSelectedLanguage().patient.recover}
                        </Alert.Button>
                    </Box>
                </Alert>
                <FormSection title={getSelectedLanguage().patient.details}>
                    <FormGroup label={getSelectedLanguage().common.nameGiven}>
                        <FormParagraph>{loadedPatient.nameGiven}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.nameInfix}>
                        <FormParagraph>{loadedPatient.nameInfix ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.nameFamily}>
                        <FormParagraph>{loadedPatient.nameFamily}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().patient.nameInfix2}>
                        <FormParagraph>{loadedPatient.nameInfix2 ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().patient.nameFamily2}>
                        <FormParagraph>{loadedPatient.nameFamily2 ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().patient.nameUsual}>
                        <FormParagraph>{loadedPatient.nameUsual ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.gender}>
                        <FormParagraph>
                            {loadedPatient.gender ? getSelectedLanguage().genderEnum[loadedPatient.gender] : '-'}
                        </FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.birthDate}>
                        <FormParagraph>{loadedPatient.birthDate}</FormParagraph>
                    </FormGroup>
                    <FormGroup
                        label={`${getSelectedLanguage().patient.identifierSocialSecurityLong} (${
                            getSelectedLanguage().patient.identifierSocialSecurity
                        })`}
                    >
                        <FormParagraph>{loadedPatient.identifierSocialSecurity ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().patient.maritalStatus}>
                        <FormParagraph>
                            {getMaritalStatusOption(loadedPatient.maritalStatus)?.label ?? '-'}
                        </FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.profession}>
                        <FormParagraph>{loadedPatient.profession ?? '-'}</FormParagraph>
                    </FormGroup>
                </FormSection>
                <FormSection title={getSelectedLanguage().common.contactDetails}>
                    <FormGroup label={getSelectedLanguage().common.street}>
                        <FormParagraph>{loadedPatient.street ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.houseNumber}>
                        <Row>
                            <Col>
                                <FormParagraph>{loadedPatient.houseNumber ?? '-'}</FormParagraph>
                            </Col>
                            <Col>
                                <FormParagraph>{loadedPatient.houseLetter}</FormParagraph>
                            </Col>
                            <Col>
                                <FormParagraph>{loadedPatient.houseNumberSuffix}</FormParagraph>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.zipcode}>
                        <FormParagraph>{loadedPatient.zip ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.city}>
                        <Row columnGap={[2, 4]}>
                            <Col>
                                <FormParagraph>{loadedPatient.city ?? '-'}</FormParagraph>
                            </Col>
                            <Col>
                                <FormParagraph>{getStateOption(loadedPatient.state)?.label}</FormParagraph>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.country}>
                        <FormParagraph>{getCountryOption(loadedPatient.country)?.label ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.telephone}>
                        <Row columnGap={[2, 4]}>
                            <Col>
                                <FormParagraph>{loadedPatient.mobileNumber ?? '-'}</FormParagraph>
                            </Col>
                            <Col>
                                <FormParagraph>{loadedPatient.homePhoneNumber ?? '-'}</FormParagraph>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().common.email}>
                        <FormParagraph>{loadedPatient.emailAddress ?? '-'}</FormParagraph>
                    </FormGroup>
                </FormSection>
                <FormSection title={getSelectedLanguage().patient.careProviderPractitioner}>
                    <FormGroup label={getSelectedLanguage().patient.careProvider}>
                        <FormParagraph>
                            {
                                getPractitionerAndOrganizationOption(
                                    loadedPatient.careProviderOrganizationId!,
                                    loadedPatient.careProviderPractitionerId!
                                )?.label
                            }
                        </FormParagraph>
                    </FormGroup>
                </FormSection>
                <FormSection title={getSelectedLanguage().patient.identifierInsurance}>
                    <FormGroup label={getSelectedLanguage().common.healthInsurer}>
                        <FormParagraph>{activeInsurance?.uzovi.name ?? '-'}</FormParagraph>
                    </FormGroup>
                    <FormGroup label={getSelectedLanguage().patient.policyNumber}>
                        <FormParagraph>{activeInsurance?.insurancePolicyNumber ?? '-'}</FormParagraph>
                    </FormGroup>
                </FormSection>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    isDisabled={isSaving}
                    onClick={() => onClose()}
                    variant="outline"
                >
                    {getSelectedLanguage().common.cancel}
                </Button>
                <Button
                    isDisabled={isSaving}
                    type="submit"
                    variant="solid"
                >
                    {getSelectedLanguage().common.save}
                </Button>
            </Modal.Footer>
        </Form>
    );
}
